import { Link, router } from '@inertiajs/react'
import { useState, type FormEvent } from 'react'
import SystemAdminLayout from '@/layouts/SystemAdminLayout'
import AdminPaginationBar from '@/components/AdminPaginationBar'
import type { AdminPagination } from '@/types'

interface AccountRow {
  id: number
  name: string
  organization_type: string | null
  plan: string
  plan_status: string
  comped: boolean
  access_active: boolean
  trial_ends_at: string | null
  currency: string
  member_count: number
  survey_count: number
  created_at: string
}

interface Props {
  accounts: AccountRow[]
  pagination: AdminPagination
  q: string | null
}

const STATUS_BADGE: Record<string, string> = {
  trialing: 'badge-info',
  active: 'badge-success',
  past_due: 'badge-warning',
  canceled: 'badge-error',
  expired: 'badge-error',
}

export default function Index({ accounts, pagination, q }: Props) {
  const [search, setSearch] = useState(q ?? '')

  const submitSearch = (e: FormEvent) => {
    e.preventDefault()
    router.get('/system_admin/accounts', search ? { q: search } : {}, { preserveState: true })
  }

  return (
    <SystemAdminLayout>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Organizations</h1>
          <p className="text-sm text-base-content/60">{pagination.total} total</p>
        </div>
        <form onSubmit={submitSearch} className="join">
          <input
            type="search"
            className="input input-sm join-item w-56"
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="submit" className="btn btn-sm btn-primary join-item">
            <span className="iconify lucide--search size-4" />
          </button>
        </form>
      </div>

      <div className="card bg-base-100">
        <div className="card-body p-0">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Trial ends</th>
                <th className="text-end">Members</th>
                <th className="text-end">Surveys</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <tr key={account.id} className="hover:bg-base-200/50">
                  <td>
                    <Link href={`/system_admin/accounts/${account.id}`} className="link link-hover font-medium">
                      {account.name}
                    </Link>
                    <div className="text-xs text-base-content/50">{account.organization_type} · {account.currency}</div>
                  </td>
                  <td>
                    <span className={`badge badge-soft badge-sm ${STATUS_BADGE[account.plan_status] ?? ''}`}>{account.plan_status}</span>
                    {account.comped && <span className="badge badge-soft badge-secondary badge-sm ms-1">comped</span>}
                  </td>
                  <td className="text-sm">{account.trial_ends_at ? new Date(account.trial_ends_at).toLocaleDateString() : <span className="text-base-content/40">—</span>}</td>
                  <td className="text-end tabular-nums">{account.member_count}</td>
                  <td className="text-end tabular-nums">{account.survey_count}</td>
                  <td className="text-sm">{new Date(account.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
              {accounts.length === 0 && (
                <tr><td colSpan={6} className="text-center text-base-content/40 py-8">No organizations match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AdminPaginationBar pagination={pagination} baseUrl="/system_admin/accounts" params={{ q: q ?? undefined }} />
    </SystemAdminLayout>
  )
}
